import type { Appointment } from '@medplum/fhirtypes';
import { getDisplayString } from '@medplum/core';
import { Button, Center, Loader, Stack, Text } from '@mantine/core';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffectivePortalPatientId } from '@/hooks/usePortalSubjectPatientId';
import { isZoomEnabled } from '@/lib/zoom';
import {
  appointmentRequiresLocationConfirmation,
  loadPatientForAddressPrefill,
  patientHomeAddressPrefill,
  saveClientLocationConfirmation,
} from '@/lib/sessionLocation/clientLocationConfirmationService';
import { resolveAppointmentModality } from '@/pages/appointments/utils/appointmentModality';
import { useTelehealthZoomMeeting } from '@/pages/session/hooks/useTelehealthZoomMeeting';
import { ClientLocationConfirmationModal } from './components/ClientLocationConfirmationModal';
import type { ClientLocationConfirmationFormInput } from '@/schemas/client-location-confirmation.schema';
import type { LocationPrefillSource } from '@/lib/sessionLocation/types';

function minutesUntil(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
}

function canJoinWindow(startIso: string): boolean {
  const mins = minutesUntil(startIso);
  return mins <= 15 && mins >= -60;
}

export function ClientTelehealthJoinPage(): JSX.Element {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const patientId = useEffectivePortalPatientId();
  const zoomEnabled = isZoomEnabled();
  const { zoom, loading: zoomLoading, waitState, checkAgain, appointment } = useTelehealthZoomMeeting(appointmentId);

  const [checkingLocation, setCheckingLocation] = useState(true);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAddressPrefill, setSavedAddressPrefill] = useState<ReturnType<typeof patientHomeAddressPrefill>>(null);
  const [fhirAppointment, setFhirAppointment] = useState<Appointment | null>(null);

  const clientName = (profile ? getDisplayString(profile) : '') || 'Client';
  const clientEmail = profile?.telecom?.find((t) => t.system === 'email')?.value ?? '';

  const meetingNumber = zoom.meetingNumber;
  const password = zoom.password;

  useEffect(() => {
    if (!appointmentId || !patientId) {
      setCheckingLocation(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setCheckingLocation(true);
      try {
        const [{ required, existing, appointment: appt }, patient] = await Promise.all([
          appointmentRequiresLocationConfirmation(medplum, appointmentId, patientId),
          loadPatientForAddressPrefill(medplum, patientId),
        ]);
        if (cancelled) return;
        setFhirAppointment(appt);
        setSavedAddressPrefill(patientHomeAddressPrefill(patient));
        if (existing || !required) {
          setLocationConfirmed(true);
          setLocationModalOpen(false);
        } else {
          setLocationModalOpen(true);
        }
      } catch {
        if (!cancelled) {
          setLocationModalOpen(true);
        }
      } finally {
        if (!cancelled) setCheckingLocation(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appointmentId, patientId, medplum]);

  const isTelehealth = useMemo(() => {
    if (!appointment) return false;
    const modality = resolveAppointmentModality(appointment);
    return modality === 'telehealth' || modality === 'hybrid';
  }, [appointment]);

  const joinAllowed = appointment?.start ? canJoinWindow(appointment.start) : false;

  const proceedToZoom = useCallback(() => {
    if (!meetingNumber) return;
    const params = new URLSearchParams({
      ...(appointmentId ? { appointmentId } : {}),
      zoomMeetingNumber: meetingNumber,
      zoomPassword: password,
      zoomUserName: clientName,
      zoomUserEmail: clientEmail,
      zoomRole: '0',
      clientJoin: '1',
    });
    window.open(`/client-portal/session/zoom-call?${params.toString()}`, '_blank', 'noopener,noreferrer');
    navigate('/client-portal');
  }, [appointmentId, meetingNumber, password, clientName, clientEmail, navigate]);

  const handleLocationConfirmed = useCallback(
    async (
      values: ClientLocationConfirmationFormInput & {
        prefillSource: LocationPrefillSource;
        wasEditedAfterPrefill: boolean;
        ipDerivedCity?: string;
        ipDerivedState?: string;
      },
    ) => {
      if (!appointmentId || !patientId) return;
      setSaving(true);
      setSaveError(null);
      try {
        const appt =
          fhirAppointment ?? (await medplum.readResource('Appointment', appointmentId));
        await saveClientLocationConfirmation(medplum, {
          appointment: appt,
          patientId,
          input: {
            street: values.street,
            city: values.city,
            state: values.state,
            postalCode: values.postalCode,
            notAtUsualAddress: values.notAtUsualAddress,
            attestationAccepted: values.attestationAccepted,
            prefillSource: values.prefillSource,
            wasEditedAfterPrefill: values.wasEditedAfterPrefill,
            ipDerivedCity: values.ipDerivedCity,
            ipDerivedState: values.ipDerivedState,
          },
        });
        setLocationConfirmed(true);
        setLocationModalOpen(false);
        proceedToZoom();
      } catch {
        setSaveError(
          'We could not save your location confirmation. Please try again before joining the session.',
        );
      } finally {
        setSaving(false);
      }
    },
    [appointmentId, patientId, fhirAppointment, medplum, proceedToZoom],
  );

  const handleJoinClick = useCallback(() => {
    if (locationConfirmed) {
      proceedToZoom();
      return;
    }
    setLocationModalOpen(true);
  }, [locationConfirmed, proceedToZoom]);

  if (!zoomEnabled) {
    return (
      <Center h="60vh">
        <Text c="dimmed">Video sessions are not available right now.</Text>
      </Center>
    );
  }

  if (!appointmentId) {
    return (
      <Center h="60vh">
        <Text c="dimmed">No appointment specified.</Text>
      </Center>
    );
  }

  if (checkingLocation || zoomLoading || waitState === 'waiting') {
    return (
      <Center h="60vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed" ta="center" maw={360}>
            Preparing your session…
          </Text>
        </Stack>
      </Center>
    );
  }

  if (!isTelehealth) {
    return (
      <Center h="60vh">
        <Text c="dimmed">This appointment is not a telehealth visit.</Text>
      </Center>
    );
  }

  if (waitState === 'timed_out') {
    return (
      <Center h="60vh">
        <Stack align="center" gap="md" maw={400}>
          <Text c="dimmed" ta="center">
            The video call link is still being prepared. You can wait a moment and try again.
          </Text>
          <Button variant="light" onClick={() => void checkAgain()}>
            Check again
          </Button>
        </Stack>
      </Center>
    );
  }

  if (!meetingNumber) {
    return (
      <Center h="60vh">
        <Text c="dimmed">No video meeting found for this appointment.</Text>
      </Center>
    );
  }

  if (!joinAllowed) {
    return (
      <Center h="60vh">
        <Text c="dimmed" ta="center" maw={400}>
          Join unlocks 15 minutes before your appointment starts.
        </Text>
      </Center>
    );
  }

  return (
    <>
      <Center h="60vh">
        <Stack align="center" gap="md">
          <Text size="lg" fw={500}>
            Ready to join your telehealth session
          </Text>
          <Button size="lg" onClick={handleJoinClick}>
            Join session
          </Button>
        </Stack>
      </Center>

      <ClientLocationConfirmationModal
        opened={locationModalOpen}
        onClose={() => {
          if (!saving) {
            setLocationModalOpen(false);
            navigate('/client-portal');
          }
        }}
        onConfirmed={handleLocationConfirmed}
        savedAddressPrefill={savedAddressPrefill}
        saving={saving}
        saveError={saveError}
      />
    </>
  );
}
